import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Floppy, Printer, Send } from 'react-bootstrap-icons';
import { Flex, ProgressCircle, Radio, RadioGroup, View, Button, Text } from '@adobe/react-spectrum';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../../../../state/store';
import { useDependency } from '../../../../../../contexts/DependencyProvider';
import { ListFPATypeDocumentParsRequest } from '../../../../../../services/soap/project/requests/ListFPATypeDocumentParsRequest';
import { ListLanguagesRequest } from '../../../../../../services/soap/translate/reqeusts/ListLanguagesRequest';
import { PrintContextRequest } from '../../../../../../services/rest/print/requests/PrintContextRequest';
import { Col, FilterCol, Order } from '../../../../../../services/soap/project/requests/ListFPARequest';
import { useReefConfig } from '../../../../../../hooks/UseReefConfig';
import { PrintDataRequest } from '../../../../../../services/rest/print/requests/PrintDataRequest';
import { getDownloadUrl } from '../../../../../../utils/ImagePathUtils';
import { invokeNewMessageCreating, openPdfContentInNewWindow, openUrlInNewWindow } from '../../../../../../utils/NavigationUtils';
import { GetContextPathRequest } from '../../../../../../services/soap/project/requests/GetContextPathRequest';
import { FPADataTypes } from '../../../../../../infra/protected/FPA/FPAData';
import styles from './print_dialog.module.css';
import { ListFolderUserRequest } from '../../../../../../services/soap/team/requests/ListFolderUserRequest';
import { ListJournalDocumentParsRequest } from '../../../../../../services/soap/finance/requests/ListJournalDocumentParsRequest';
import { GetContactListRequest } from '../../../../../../services/soap/main/requests/GetContactListRequest';

interface PrintDialogProps {
  open?: boolean;
  onClose?: () => void;
  onSend?: () => void;
  onPrint?: () => void;
  onSave?: () => void;
  selectedItem?: any;
  print_type: string;
}

const PrintDialog: React.FC<PrintDialogProps> = ({ open, onClose, onSend, onPrint, onSave, selectedItem, print_type }) => {
  const INVOICE_DEFAULT_TEMPLATE_OPTION = '__JOURNAL_DEFAULT__';
  const invoicePrintTypes = new Set([
    'invoice',
    'invoice_received',
    'sales_order',
    'purchase_order',
    'estimate',
    'offer_received',
  ]);
  const sortTemplatesByPriority = (templates: any[] = []) =>
    [...templates].sort((left: any, right: any) => {
      const leftPriority = Number(left?.priority ?? Number.MAX_SAFE_INTEGER);
      const rightPriority = Number(right?.priority ?? Number.MAX_SAFE_INTEGER);
      if (leftPriority !== rightPriority) {
        return leftPriority - rightPriority;
      }

      const leftDescription = String(left?.description ?? '').toLowerCase();
      const rightDescription = String(right?.description ?? '').toLowerCase();
      return leftDescription.localeCompare(rightDescription);
    });
  const isDefaultTemplateFlag = (value: any) => {
    const normalized = String(value ?? '').trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  };
  const formSignatures = useSelector((state: RootState) => state.userWebLayout.formSignatures);
  const { store, projectService, translateService, printRestService, teamService, financeService, mainService } = useDependency();
  const { baseUrl, print_service } = useReefConfig();
  const [docTemplates, setDocTemplates] = useState<any>([]);
  const [langList, setLangList] = useState<any>([]);
  const [langName, setLangName] = useState<string>('');
  const [docType, setDocType] = useState<string>('pdf');
  const [selectDocumentID, setSelectDocumentID] = useState<any>('');
  const [invoiceDefaultTemplateId, setInvoiceDefaultTemplateId] = useState<string>('');
  const [invoiceDefaultTemplateLabel, setInvoiceDefaultTemplateLabel] = useState<string>('');
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const effectivePrintType = print_type || selectedItem?.type || '';
  const isInvoicePrint = invoicePrintTypes.has(effectivePrintType);
  const normalizedTemplateId = (() => {
    const selected = selectDocumentID ? String(selectDocumentID).trim() : '';
    if (isInvoicePrint && selected === INVOICE_DEFAULT_TEMPLATE_OPTION) {
      return String(invoiceDefaultTemplateId || '').trim();
    }
    return selected;
  })();
  const { t } = useTranslation();

  const _t = (key: string) => t(`printDialog.${key}`, { ns: 'finder' });

  useEffect(() => {
    const initializeDialog = async () => {
      try {
        setError(null);
        setShowLoader(true);

        // Execute operations in parallel for better performance
        const results = await Promise.allSettled([loadDocumentParams(), loadLanguage()]);

        // Check for failures and handle them appropriately
        const failures = results.map((result, index) => ({ result, operation: index === 0 ? 'loadDocumentParams' : 'loadLanguage' })).filter(({ result }) => result.status === 'rejected');

        if (failures.length > 0) {
          const errorMessages = failures.map(({ operation, result }) => `${operation}: ${result.status === 'rejected' ? result.reason?.message || 'Unknown error' : ''}`);
          throw new Error(`Failed to initialize: ${errorMessages.join(', ')}`);
        }
      } catch (error) {
        console.error('PrintDialog initialization failed:', error);

        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setError(error instanceof Error ? error.message : 'Failed to load dialog data');
        }
      } finally {
        // Only update loader state if component is still mounted
        if (isMountedRef.current) {
          setShowLoader(false);
        }
      }
    };

    // Set mounted ref to true when effect runs
    isMountedRef.current = true;

    // Initialize the dialog
    initializeDialog();

    // Cleanup function to prevent memory leaks
    return () => {
      isMountedRef.current = false;
    };
  }, [selectedItem?.item_type_id, selectedItem?.journal, effectivePrintType]);

  const loadDocumentParams = useCallback(async () => {
    if (isInvoicePrint) {
      const journalId = String(selectedItem?.journal ?? '').trim();
      if (!journalId) {
        if (isMountedRef.current) {
          setDocTemplates([]);
          setInvoiceDefaultTemplateId('');
          setInvoiceDefaultTemplateLabel('');
          setSelectDocumentID('');
        }
        return;
      }

      try {
        const response = await financeService.listJournalDocumentPars(
          new ListJournalDocumentParsRequest(store.Server, store.SessionId, {
            limit: 10,
            offset: 0,
            FILTER: [
              new FilterCol('journal', 'equals', journalId)
            ],
            ORDER: new Order([new Col('priority'), new Col('description')]),
          })
        );

        const templates = sortTemplatesByPriority(Array.isArray(response?.JOURNAL_DOCUMENT_PAR)
          ? response.JOURNAL_DOCUMENT_PAR
          : response?.JOURNAL_DOCUMENT_PAR
            ? [response.JOURNAL_DOCUMENT_PAR]
            : []);

        const defaultTemplate = templates.find((template: any) => isDefaultTemplateFlag(template?.isDefault));
        const defaultTemplateId = defaultTemplate?.document ? String(defaultTemplate.document) : '';
        const customTemplates = defaultTemplateId
          ? templates.filter((template: any) => String(template?.document ?? '') !== defaultTemplateId)
          : templates;

        if (isMountedRef.current) {
          setDocTemplates(customTemplates);
          setInvoiceDefaultTemplateId(defaultTemplateId);
          setInvoiceDefaultTemplateLabel(String(defaultTemplate?.description || '').trim());
          setSelectDocumentID(defaultTemplateId ? INVOICE_DEFAULT_TEMPLATE_OPTION : '');
        }
        return;
      } catch (error) {
        console.error('Failed to load journal document parameters:', error);
        if (isMountedRef.current) {
          setDocTemplates([]);
          setInvoiceDefaultTemplateId('');
          setInvoiceDefaultTemplateLabel('');
          setSelectDocumentID('');
        }
        return;
      }
    }

    if (!selectedItem?.item_type_id) {
      if (isMountedRef.current) {
        setDocTemplates([]);
        setInvoiceDefaultTemplateId('');
        setInvoiceDefaultTemplateLabel('');
        setSelectDocumentID('');
      }
      return;
    }

    try {
      const resDocPars = await projectService.listFPATypeDocumentPars(
        new ListFPATypeDocumentParsRequest(store.Server, store.SessionId, {
          limit: 50,
          offset: 0,
          FILTER: [new FilterCol('type', 'equals', selectedItem.item_type_id)],
          ORDER: new Order([new Col('priority'), new Col('description')]),
        })
      );

      if (resDocPars.result !== 'OK' || !resDocPars?.FPA_TYPE_DOCUMENT_PAR?.length) {
        // No templates available, but this isn't necessarily an error
        setDocTemplates([]);
        setSelectDocumentID('');
        return;
      }

      const templates = sortTemplatesByPriority(resDocPars.FPA_TYPE_DOCUMENT_PAR);
      const defaultDocument = templates.find((doc: any) => isDefaultTemplateFlag(doc?.isDefault));

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setDocTemplates(templates);
        setInvoiceDefaultTemplateId('');
        setInvoiceDefaultTemplateLabel('');
        setSelectDocumentID(defaultDocument?.document ? String(defaultDocument.document) : '');
      }
    } catch (error) {
      console.error('Failed to load document parameters:', error);
      throw new Error(`Document templates loading failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [effectivePrintType, isInvoicePrint, selectedItem?.item_type_id, selectedItem?.journal, store.Server, store.SessionId, projectService, financeService]);

  const loadLanguage = useCallback(async () => {
    try {
      const langResp = await translateService.listLanguages(new ListLanguagesRequest(store.Server, store.SessionId));
      //console.log('langResp.result', langResp);
      if (langResp.result !== 'OK' || !langResp?.LANGUAGE?.length) {
        // Set default language only
        if (isMountedRef.current) {
          setLangList([
            {
              languageShort: '',
              languageLong: _t('default'),
            },
          ]);
        }
        return;
      }

      const availableLanguages = langResp.LANGUAGE.filter((lang: any) => lang.useForTranslate === '1');
      const languageOptions = [
        {
          languageShort: '',
          languageLong: _t('default'),
        },
        ...availableLanguages,
      ];

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setLangList(languageOptions);
      }
    } catch (error) {
      console.error('Failed to load languages:', error);
      throw new Error(`Languages loading failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [store.Server, store.SessionId, translateService, _t]);

  function toBase64(arrBuffer: any) {
    const base64 = btoa(String.fromCharCode(...new Uint8Array(arrBuffer)));
    return `data:application/pdf;base64,${base64}`;
  }

  const resolveFolderFromContextPath = useCallback(async (contextId: string, fallbackFolderId: string) => {
    const trimmedContextId = String(contextId || '').trim();
    const trimmedFallbackFolderId = String(fallbackFolderId || '').trim();
    if (!trimmedContextId || !Number.isFinite(Number(trimmedContextId))) {
      return trimmedFallbackFolderId;
    }

    try {
      const response: any = await projectService.getContextPath(
        new GetContextPathRequest(store.Server, store.SessionId, Number(trimmedContextId))
      );
      const list = Array.isArray(response?.LIST) ? response.LIST : [];
      const folderContext = list.find((x: any) => String(x?.CONTEXT?.type ?? '').toUpperCase() === 'FOLDER')?.CONTEXT;
      return String(folderContext?.id ?? trimmedFallbackFolderId ?? '').trim();
    } catch (error) {
      console.error('Failed to resolve folder context for send:', error);
      return trimmedFallbackFolderId;
    }
  }, [projectService, store.Server, store.SessionId]);

  const resolveMessageTarget = useCallback(async () => {
    if (!selectedItem) {
      return {
        contextId: '',
        contextType: '',
        folderId: '',
        isFolderContext: false,
      };
    }

    if (isInvoicePrint) {
      const activityId = String(selectedItem?.activity ?? '').trim();
      const projectId = String(selectedItem?.project ?? '').trim();
      const folderId = String(selectedItem?.folder ?? '').trim();

      if (activityId) {
        return {
          contextId: activityId,
          contextType: FPADataTypes.ACTIVITY,
          folderId: await resolveFolderFromContextPath(activityId, folderId),
          isFolderContext: false,
        };
      }

      if (projectId) {
        return {
          contextId: projectId,
          contextType: FPADataTypes.PROJECT,
          folderId: await resolveFolderFromContextPath(projectId, folderId),
          isFolderContext: false,
        };
      }

      if (folderId) {
        return {
          contextId: folderId,
          contextType: FPADataTypes.FOLDER,
          folderId,
          isFolderContext: true,
        };
      }
    }

    const selectedContextId = String(selectedItem?.id ?? '').trim();
    let context: any = null;
    if (selectedContextId && Number.isFinite(Number(selectedContextId))) {
      try {
        const response: any = await projectService.getContextPath(
          new GetContextPathRequest(store.Server, store.SessionId, Number(selectedContextId))
        );
        const list = Array.isArray(response?.LIST) ? response.LIST : [];
        const index = selectedItem.type === FPADataTypes.ACTIVITY ? 1 : 0;
        context = list[index]?.CONTEXT;
      } catch (error) {
        console.error('Failed to resolve context path for send:', error);
      }
    }

    return {
      contextId: selectedContextId,
      contextType: selectedItem?.type || '',
      folderId: String(
        selectedItem.type === FPADataTypes.FOLDER ? context?.id : context?.parentId ?? ''
      ).trim(),
      isFolderContext: selectedItem.type === FPADataTypes.FOLDER,
    };
  }, [selectedItem, isInvoicePrint, projectService, resolveFolderFromContextPath, store.Server, store.SessionId]);

  const handleSendClick = async () => {
    if (isSending || isPrinting || isSaving) return;
    
    setIsSending(true);
    setShowLoader(true);
    
    try {
      const messageTarget = await resolveMessageTarget();
      const messageContextId = String(messageTarget?.contextId || selectedItem?.id || '').trim();
      const messageContextType = String(messageTarget?.contextType || '').trim();
      const folderID = String(messageTarget?.folderId ?? '').trim();
      const listFolderUser =
        folderID && Number.isFinite(Number(folderID))
          ? await teamService.listFolderUser(
              new ListFolderUserRequest(store.Server, store.SessionId, Number(folderID))
            )
          : null;
      
      const folderUsers = Array.isArray(listFolderUser?.ROWS?.ROW)
        ? listFolderUser.ROWS.ROW
        : listFolderUser?.ROWS?.ROW
          ? [listFolderUser.ROWS.ROW]
          : [];
      const folderUserContactIds = folderUsers
        .map((user: any) => String(user?.contact ?? '').trim())
        .filter(Boolean);
      const resolvedUsers =
        folderUserContactIds.length > 0
          ? await mainService.getContactList(
              new GetContactListRequest(store.Server, store.SessionId, {
                offset: 0,
                limit: folderUserContactIds.length,
                onlyIds: false,
                showOnlyUc: false,
                onlyUser: true,
                conjuctionDistrGroups: 0,
                USERS: {
                  ID: folderUserContactIds,
                },
              })
            )
          : null;
      const resolvedUsersByContactId = new Map<string, any>(
        Array.isArray(resolvedUsers?.CONTACT)
          ? resolvedUsers.CONTACT.map((contact: any) => [String(contact?.id), contact])
          : []
      );
      const notifyUsers = folderUsers
        .map((user: any) => {
          const contactId = String(user?.contact ?? '').trim();
          const resolvedUser = resolvedUsersByContactId.get(contactId);
          const sysUserId = String(resolvedUser?.sysUser ?? '').trim();
          const userName = String(resolvedUser?.sysUserName ?? user?.userName ?? '').trim();
          const contactName = String(
            resolvedUser
              ? `${resolvedUser?.name3 !== undefined ? resolvedUser.name3 : ''} ${resolvedUser?.name1 ?? ''}`.trim()
              : user?.userName ?? ''
          ).trim();

          if (!sysUserId) return null;

          return {
            contactName,
            userName,
            sysUserId,
            id: contactId,
            label: contactName,
            value: contactId,
            contact: contactId,
          };
        })
        .filter(Boolean);

      // console.log('print_type', print_type);
      // console.log('docType', docType);

      // Handle different document types similar to onPrintClick
      if (docType === 'template') {
        // Template documents require different handling for sending
        // Templates cannot be sent as attachments since they return URLs, not base64 data
        // We need to use printContext instead for template-based sending
        const response = await printRestService.printContext(
          new PrintContextRequest(
            store.Server,
            store.SessionId,
            effectivePrintType,
            String(selectedItem?.id),
            'pdf', // Convert template to PDF for sending
            { signatures: formSignatures },
            '',
            normalizedTemplateId || undefined,
            langName
          )
        );
        
        const newFile = {
          id: selectedItem?.id,
          fileName: `Template_${selectedItem?.id}.pdf`,
          fileSize: 0,
          base64Files: response,
        };
        
        invokeNewMessageCreating(
          messageContextId,
          messageContextType,
          [newFile],
          notifyUsers,
          messageTarget.isFolderContext
        );
        
      } else if (docType === 'json') {
        // Handle JSON data type for sending
        const printDataBody = {
          print_type: effectivePrintType,
          object_id: selectedItem.id,
          language: langName,
          signatures: formSignatures,
        };

        const response = await printRestService.printData(
          new PrintDataRequest(store.Server, store.SessionId, printDataBody),
          { signatures: formSignatures }
        );
        
        const data_response = response.data;
        const signatures_data = data_response.signatures;
        let jsonString = JSON.stringify(data_response, null, 2);
        
        if (signatures_data) {
          jsonString = jsonString.split('"signatures": {}').join(`"signatures": ${JSON.stringify(signatures_data, null, 2)}`);
        }
        
        // Create a JSON file attachment with proper Unicode support
        const encoder = new TextEncoder();
        const uint8Array = encoder.encode(jsonString);
        const base64String = btoa(String.fromCharCode(...uint8Array));
        
        const newFile = {
          id: selectedItem?.id,
          fileName: `Data_${selectedItem?.id}.json`,
          fileSize: new Blob([jsonString]).size,
          base64Files: base64String, // Convert JSON string to base64 with Unicode support
        };
        
        invokeNewMessageCreating(
          messageContextId,
          messageContextType,
          [newFile],
          notifyUsers,
          messageTarget.isFolderContext
        );
        
      } else {
        // Handle standard document types (pdf, docx, xlsx)
        const response = await printRestService.printContext(
          new PrintContextRequest(
            store.Server,
            store.SessionId,
            effectivePrintType,
            String(selectedItem?.id),
            docType,
            { signatures: formSignatures },
            '',
            normalizedTemplateId || undefined,
            langName
          )
        );
        
        const fileExtension = docType === 'docx' ? 'docx' : docType === 'xlsx' ? 'xlsx' : 'pdf';
        const newFile = {
          id: selectedItem?.id,
          fileName: `Attachment_${selectedItem?.id}.${fileExtension}`,
          fileSize: 0,
          base64Files: response,
        };
        
        invokeNewMessageCreating(
          messageContextId,
          messageContextType,
          [newFile],
          notifyUsers,
          messageTarget.isFolderContext
        );
      }
      
      // Call the optional onSend callback if provided
      await onSend?.();
      
    } catch (error) {
      console.error('Send operation failed:', error);
      setError(error instanceof Error ? error.message : 'Failed to send document');
    } finally {
      setIsSending(false);
      setShowLoader(false);
    }
  };

  const onPrintClick = () => {
    if (isSending || isPrinting || isSaving) return;
    setIsPrinting(true);
    if (docType === 'template') {
      let printDataRequest = new PrintDataRequest(store.Server, store.SessionId, {
        print_type: effectivePrintType,
        object_id: selectedItem.id,
        template_id: normalizedTemplateId || undefined,
        language: langName,
      });

      let printData = printRestService.printDataTemplate(printDataRequest);
      let windowReference: any = window.open('', '_blank', 'scrollbars=yes,status=yes,menubar=no,toolbar=no');
      windowReference?.location.replace(`${print_service.uri}${printData}`);
      setIsPrinting(false);
      return;
    }

    if (docType === 'json') {
      let printDataBody = {
        print_type: effectivePrintType,
        object_id: selectedItem.id,
        language: langName,
        signatures: formSignatures,
      };

      printRestService
        .printData(new PrintDataRequest(store.Server, store.SessionId, printDataBody), { signatures: formSignatures })
        .then(async (response: any) => {
          var data_response = response.data;
          var signatures_data = data_response.signatures;
          var jsonString = JSON.stringify(data_response, null, 2);
          if (signatures_data) {
            jsonString = jsonString.split('"signatures": {}').join(`"signatures": ${JSON.stringify(signatures_data, null, 2)}`);
          }
          let windowReference: any = window.open('', '_blank', 'scrollbars=yes,status=yes,menubar=no,toolbar=no');
          windowReference?.document.write(`
          <!DOCTYPE html>
        <html>
        <head>
            <title>JSON Data</title>
            <style>
                body { font-family: monospace; }
            </style>
        </head>
        <body>
            <pre>${jsonString}</pre>
        </body>
        </html>
        `);
        })
        .catch(error => {
          console.error('Print JSON preview failed:', error);
        })
        .finally(() => {
          setIsPrinting(false);
        });
    } else {
      //console.log('print_type', print_type);
      printRestService
        .printContext(new PrintContextRequest(store.Server, store.SessionId, effectivePrintType, String(selectedItem?.id), docType, { signatures: formSignatures }, ``, normalizedTemplateId || undefined, langName))
        .then(async (response: any) => {
          // Handle different document types appropriately
          if (docType === 'pdf') {
            // Only open PDF files with PDF viewer
            openPdfContentInNewWindow(response);
          } else {
            // For other document types (docx, xlsx), create a download blob
            let mimeType = 'application/octet-stream';
            let fileName = `Document_${selectedItem?.id}`;
            
            if (docType === 'docx') {
              mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
              fileName += '.docx';
            } else if (docType === 'xlsx') {
              mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
              fileName += '.xlsx';
            }
            
            // Convert base64 to blob and trigger download
            const binaryString = atob(response);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: mimeType });
            const url = URL.createObjectURL(blob);
            
            // Create download link
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }
        })
        .catch(error => {
          console.error('Print context preview failed:', error);
        })
        .finally(() => {
          setIsPrinting(false);
          setShowLoader(false);
        });
    }
  };

  const onSaveClick = () => {
    if (isSending || isPrinting || isSaving) return;
    if (docType === 'template') {
      return;
    }
    setIsSaving(true);
    printRestService
      .printDocument(new PrintContextRequest(store.Server, store.SessionId, effectivePrintType, String(selectedItem?.id), docType, { signatures: formSignatures }, ``, normalizedTemplateId || undefined))
      .then(async (response: any) => {
        let strURL = getDownloadUrl(response.documentId, baseUrl, store.Server, store.SessionId);
        openUrlInNewWindow(strURL);
      })
      .catch(error => {
        console.error('Save document failed:', error);
      })
      .finally(() => {
        setIsSaving(false);
        setShowLoader(false);
      });
  };

  if (error) {
    return (
      <View width={'100%'} padding="size-200">
        <Flex direction="column" alignItems="center" gap="size-100">
          <Text>⚠️ {_t('error_loading_data')}</Text>
          <Text UNSAFE_style={{ fontSize: '12px', color: '#666' }}>{error}</Text>
          <Button
            variant="secondary"
            onPress={() => {
              setError(null);
              setShowLoader(true);
              // Re-trigger the effect by updating a dependency
              // You might want to add a retry counter here
            }}
          >
            {_t('retry')}
          </Button>
        </Flex>
      </View>
    );
  }

  if (showLoader) {
    return (
      <Flex width="100%" justifyContent={'center'} marginTop={10}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  } else {
    return (
      <View width={'100%'} position={'relative'} height={'auto'} UNSAFE_className={styles.print_dialog_parent}>
        {(isInvoicePrint || docTemplates.length > 0) && (
          <>
            <Flex position={'relative'} height={'30px'} UNSAFE_className={styles.doc_template_heading} alignContent={'center'} alignItems={'center'} justifyContent={'start'}>
              {_t('document_template')}
            </Flex>
            <Flex direction={'row'} position={'relative'} alignItems={'center'} justifyContent={'start'} gap={'32px'}>
              <RadioGroup label="" value={selectDocumentID} onChange={(value: any) => setSelectDocumentID(value)} orientation="vertical">
                {isInvoicePrint && invoiceDefaultTemplateId && (
                  <Radio value={INVOICE_DEFAULT_TEMPLATE_OPTION}>
                    {invoiceDefaultTemplateLabel || _t('default')}
                  </Radio>
                )}
                <Radio value="">{_t('system_default')}</Radio>
                {docTemplates.map((docTemplate: any) => (
                  <Radio key={docTemplate.id} value={String(docTemplate.document)}>
                    {docTemplate.description}
                  </Radio>
                ))}
              </RadioGroup>
            </Flex>
          </>
        )}

        <Flex position={'relative'} height={'30px'} UNSAFE_className={styles.doc_template_heading} alignContent={'center'} alignItems={'center'} justifyContent={'start'}>
          {_t('document_type')}
        </Flex>
        <Flex direction={'row'} position={'relative'} alignItems={'center'} justifyContent={'start'} gap={'32px'}>
          <RadioGroup label="" orientation="horizontal" defaultValue={'pdf'} onChange={value => setDocType(value)}>
            <Radio value="pdf">{_t('pdf')}</Radio>
            <Radio value="docx">{_t('word_doc')}</Radio>
            <Radio value="xlsx">{_t('excel_doc')}</Radio>
            <Radio value="json">{_t('json')}</Radio>
            <Radio value="template">{_t('template')}</Radio>
          </RadioGroup>
        </Flex>

        <Flex position={'relative'} height={'30px'} UNSAFE_className={styles.doc_template_heading} alignContent={'center'} alignItems={'center'} justifyContent={'start'}>
          {_t('document_language')}
        </Flex>
        <Flex direction={'row'} position={'relative'} alignItems={'center'} justifyContent={'start'} gap={'32px'}>
          <RadioGroup label="" orientation="horizontal" onChange={value => setLangName(value)} defaultValue={''}>
            {langList.map((lang: any, index: number) => (
              <Radio key={lang.isoShort || lang.languageShort || `lang-${index}`} value={lang.isoShort || lang.languageShort || ''}>
                {lang.languageLong}
              </Radio>
            ))}
          </RadioGroup>
        </Flex>

        <View position={'relative'} bottom={'0px'} left={'0px'} width={'100%'} height={'55px'} UNSAFE_className={styles.footer}>
          <Flex direction={'row'} position={'absolute'} bottom={'0px'} left={'calc(50% - 150px)'} alignItems={'start'} justifyContent={'start'} gap={'10px'}>
            <Flex
              direction={'row'}
              width={'90px'}
              height={'40px'}
              flexShrink={0}
              alignItems={'center'}
              justifyContent={'center'}
              gap={'8px'}
              UNSAFE_className={`${styles.footer_btn} ${isSending || isPrinting || isSaving ? styles.disabled : ''}`}
            >
              <div style={{ display: 'contents' }} onClick={handleSendClick}>
                {isSending ? <ProgressCircle size="S" aria-label="Sending..." isIndeterminate /> : <Send size={32} />}
                <View UNSAFE_className={styles.btn_label}>{_t('send')}</View>
              </div>
            </Flex>
            <Flex
              direction={'row'}
              width={'90px'}
              height={'40px'}
              flexShrink={0}
              alignItems={'center'}
              justifyContent={'center'}
              gap={'8px'}
              UNSAFE_className={`${styles.footer_btn} ${isSending || isPrinting || isSaving ? styles.disabled : ''}`}
            >
              <div style={{ display: 'contents' }} onClick={onPrintClick}>
                {isPrinting ? <ProgressCircle size="S" aria-label="Printing..." isIndeterminate /> : <Printer size={16} />}
                <View UNSAFE_className={styles.btn_label}>{_t('print')}</View>
              </div>
            </Flex>
            <Flex
              direction={'row'}
              width={'90px'}
              height={'40px'}
              flexShrink={0}
              alignItems={'center'}
              justifyContent={'center'}
              gap={'8px'}
              UNSAFE_className={`${styles.footer_btn} ${isSending || isPrinting || isSaving ? styles.disabled : ''}`}
            >
              <div style={{ display: 'contents' }} onClick={onSaveClick}>
                {isSaving ? <ProgressCircle size="S" aria-label="Saving..." isIndeterminate /> : <Floppy size={32} />}
                <View UNSAFE_className={styles.btn_label}>{_t('save')}</View>
              </div>
            </Flex>
          </Flex>
        </View>
      </View>
    );
  }
};

export default PrintDialog;
